import React, { useEffect, useMemo, useState } from 'react'
import { ScrollView, Text, useWindowDimensions, View } from 'react-native'
import { useCssStyle } from '../../customHook/getCssStyle';
import CustomPressable from '../../components/customPressable/customPressable';
import CustomFlatList from '../../components/customFlatList';
import { ImageComp } from '../../components/Image/Image';
import DatePickerModal from '../../components/datePickerModal/datePickerModal';
import { useSelector } from 'react-redux';
import { formatDate } from '../../js/formatDate';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { CustomTextInput } from '../../components/customTextInput';
import HeaderWithBack from '../../components/Header/headerWithBack';
import RenderHTML from 'react-native-render-html';
import { CHEVRON_RIGHT } from '../../assets';

const ActivitiesEventList = (props) => {
    const getStyles = useCssStyle();
    const { loading, events, categories = [], currentPage, selectedEvent, setSelectedEvent, handleNext, handleGoBack } = props;

    const { t } = useTranslation();
    const { width } = useWindowDimensions()

    const { extraConfigParam } = useSelector((state) => state.deviceConfigSlice);
    const dateFormat = extraConfigParam?.DATE_FORMAT;

    const isNonRecurringEvent = Boolean (selectedEvent?.event?.kind=="event" && selectedEvent?.eventInfo.firstAvailableDate && selectedEvent?.eventInfo.firstAvailableDate.scheduledDate);

    const [filter, setFilter] = useState('all');
    const updatedCategories = useMemo(() => {
        if (Array.isArray(categories) && categories.length) {
            return [{ name: "All", id: "all" }, ...categories];
        }
        return [];
    }, [categories]);


    const flattenedEvents = useMemo(() => {
        if (!events) return [];
        if (Array.isArray(events)) {
            return events.map((it) => ({
                ...it,
                __category: it.__category || it.resourceGroupCollectionId || 'uncategorized',
            }));
        }
        if (typeof events === 'object') {
            const arr = [];
            Object.entries(events).forEach(([categoryKey, items]) => {
                if (!Array.isArray(items)) return;
                items.forEach((item) => {
                    arr.push({ ...item, __category: categoryKey });
                });
            });
            return arr;
        }
        return [];
    }, [events]);


    const visibleEvents = useMemo(() => {
        if (!updatedCategories.length) return flattenedEvents;
        if (filter === 'all') return flattenedEvents;
        return flattenedEvents.filter((ev) =>
            ev.__category === filter ||
            ev.category === filter ||
            ev.type === filter ||
            ev.resourceGroupCollectionId === filter
        );
    }, [flattenedEvents, filter, updatedCategories]);

    const getCategory = (event) => {
        return categories.filter((c) =>
            event.__category === c.id ||
            event.category === c.id ||
            event.type === c.id ||
            event.resourceGroupCollectionId === c.id
        );
    }

    useEffect(()=>{
        if(isNonRecurringEvent){
            setSelectedEvent((prev)=> {
                return {
                    ...prev,
                    selectedDate: moment(selectedEvent?.eventInfo?.firstAvailableDate?.scheduledDate).format('YYYY-MM-DD')
                }
            })
        }
        
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isNonRecurringEvent])



    const handleCategoryClick = (category) => {
        setFilter(category.id);
    }

    useEffect(() => {
        if (!updatedCategories.length) {
            setFilter('all');
            return;
        }
        const exists = updatedCategories.some((c) => c.id === filter);
        if (!exists) setFilter('all');
    }, [updatedCategories, filter]);



    const renderEventItem = ({ item }) => {
        const imageUri =
            item.coverMedia?.uri?.original ||
            item.coverMedia?.uri?.['1080w'] ||
            item.coverMedia?.uri?.['750w'] ||
            item.coverMedia?.uri?.['640w'] ||
            (Array.isArray(item.pictures) && item.pictures[0]?.uri?.original) ||
            null;

        const listItemDescription = getStyles([selectedEvent ? 'list_itemContainer_textContainer_subtitle' : "", 'activitiesEventListItemDescription']);

        return (
            <CustomPressable
                onPress={() => handleNext(item, getCategory(item))}
                style={getStyles([selectedEvent ? "list_itemContainer" : "", 'activitiesEventListItemContainer'])}
                key={item.id ?? `${item.title}`}
                id={'activitiesEventListItemContainer'}
            >
                {imageUri ? (
                    <ImageComp
                        source={imageUri}
                        style={getStyles([selectedEvent ? 'list_itemContainer_image' : "", 'activitiesEventListItemImage' ])}
                        id={!selectedEvent ? 'activitiesEventListItemImage' : 'activitiesSubEventListItemImage'}
                    />
                ) : null}

                <View style={getStyles([selectedEvent ?  'list_itemContainer_textContainer' : "", 'activitiesEventListItemContent'])} id='activitiesEventListItemContent'>
                    
                    <Text numberOfLines={2} style={getStyles([selectedEvent ?  'list_itemContainer_textContainer_title' : "", 'activitiesEventListItemTitle'])} id='list_itemContainer_textContainer_title activitiesEventListItemTitle'>
                        {item.title ?? item.name ?? 'Untitled'}
                    </Text>

                    {item?.startingPrice && item?.supportedCurrencies ? <Text style={getStyles([selectedEvent ?  'list_itemContainer_textContainer_subtitle' : "", 'activitiesEventListItemSubtitle'])} id='list_itemContainer_textContainer_subtitle activitiesEventListItemSubtitle'>
                        Starting from : {item.supportedCurrencies[0] + " " + parseFloat(item.startingPrice).toFixed(2)}
                    </Text> : null}
                </View>
            </CustomPressable>
        );
    };


    return (
        <View style={getStyles(["widgetRootContainer", "activitiesEventListRootContainer"])} id='activitiesEventListRootContainer'>
            <HeaderWithBack handleCustomBackPress={handleGoBack} />


            <ScrollView
                automaticallyAdjustKeyboardInsets={true} 
                contentContainerStyle={getStyles(['widgetmainContainer', 'activitiesEventListMainContainer'])} 
                showsVerticalScrollIndicator={false}
                id='activitiesBookingMainContainer'
            >
                <View style={getStyles(['widgetmainContainerHeaderContainer', 'activitiesEventListMainHeaderContainer'])} id='activitiesEventListMainHeaderContainer'>
                    <View style={getStyles(['widgetmainContainerHeaderTitleContainer', 'activitiesBookingHeaderTitleContainer'])} id='activitiesBookingHeaderTitleContainer'>
                        <Text style={getStyles(['widgetmainContainerHeaderTitle', 'activitiesBookingEventListHeaderTitle'])} id='activitiesBookingEventListHeaderTitle'>Discover Experiences</Text>
                    </View>

                    {Array.isArray(categories) && categories?.length ? 
                        <ScrollView 
                            horizontal 
                            showsHorizontalScrollIndicator={false} 
                            contentContainerStyle={getStyles(['activitiesEventListHeaderContainer'])} 
                            id="activitiesEventListHeaderContainer" 
                            style={{ width: '100%' }}
                        >
                        {updatedCategories?.map((category) => (
                            <CustomPressable
                                key={category.id}
                                onPress={() => handleCategoryClick(category)}
                                style={getStyles(["activitiesEventListFilterContainer", category.id === filter ? "activitiesEventListFilterContainerActive" : "" ])}
                                id="activitiesEventListFilterContainer activitiesEventListFilterContainerActive"
                            >
                                <Text 
                                    style={getStyles(["activitiesEventListFilterName", category.id === filter ? "activitiesEventListFilterNameActive" : ""])}
                                    id='activitiesEventListFilterName activitiesEventListFilterNameActive'
                                >
                                    {category.name}
                                </Text>
                            </CustomPressable>
                        ))}
                    </ScrollView> : null}
                </View>

                <View style={getStyles(['widgetmainContainerChildContainer', 'activitiesEventListMainContainerChildContainer', categories?.length ? "activitiesEventListMainContainerChildContainer2" : ""])} id='activitiesEventListMainContainerChildContainer'>

                    {(events && !selectedEvent) && (<>
                                {visibleEvents.length === 0 ? (
                                    null
                                ) : (
                                    <View style={getStyles(['activitiesEventListContainer'])}> 
                                        <CustomFlatList
                                            data={visibleEvents}
                                            keyExtractor={(item) => item.id ?? `${item.title}-${Math.random()}`}
                                            renderItem={renderEventItem}
                                            showsVerticalScrollIndicator={false}
                                        />
                                    </View>
                                )}
                            </>
                        )}
                </View>

            </ScrollView>
        </View>
    )
}

export default ActivitiesEventList
